// Small hooks shared by several screens.
import { useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { AppState } from 'react-native';
import { useFocusEffect, useNavigation } from '@react-navigation/native';
import { localDay } from './logic/dates';
import { formatInCalendar, type CalendarId } from './logic/calendars';
import { dietConflicts, dietFlags, dietReview, type DietFlags, type DietStatus } from './logic/diets';
import { dietsWithAllergies } from './logic/dietPlan';
import { holidaysNear, type HolidayNear } from './logic/holidays';
import { deviceLanguage, locale } from './i18n';
import { useAllIngredients, useLibrary } from './store/library';
import { useSettings } from './store/settings';
import type { Day, Diet, Ingredient, Recipe } from './types';

/** Today's local date; refreshes when the screen regains focus or the app returns to foreground. */
export function useToday(): Day {
  const [today, setToday] = useState(localDay());
  useFocusEffect(
    useCallback(() => {
      setToday(localDay());
      const sub = AppState.addEventListener('change', s => s === 'active' && setToday(localDay()));
      return () => sub.remove();
    }, []),
  );
  return today;
}

/** Sets header title / buttons for the current screen. */
export function useHeader(options: Record<string, unknown>, deps: unknown[]) {
  const navigation = useNavigation();
  useLayoutEffect(() => {
    navigation.setOptions(options);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigation, ...deps]);
}

const DAY_FORMAT: Intl.DateTimeFormatOptions = { weekday: 'short', day: 'numeric', month: 'short' };

/** A day as text, in the calendar from settings (Gregorian unless the user picked another). */
export function formatDay(day: Day, opts: Intl.DateTimeFormatOptions = DAY_FORMAT, calendar: CalendarId = useSettings.getState().calendar): string {
  return formatInCalendar(day, opts, calendar, locale());
}

/** `formatDay` for components: re-renders them when the calendar setting changes. */
export function useFormatDay(): (day: Day, opts?: Intl.DateTimeFormatOptions) => string {
  const calendar = useSettings(s => s.calendar);
  return useCallback((day: Day, opts: Intl.DateTimeFormatOptions = DAY_FORMAT) => formatDay(day, opts, calendar), [calendar]);
}

/** Currency for price estimates, from the device region (Swift used Locale.current.currency). */
export function currencyCode(): string {
  const region = (deviceLanguage().split('-')[1] ?? '').toUpperCase();
  const map: Record<string, string> = { CH: 'CHF', LI: 'CHF', US: 'USD', GB: 'GBP', CA: 'CAD', AU: 'AUD', NZ: 'NZD', JP: 'JPY', IN: 'INR' };
  return map[region] ?? 'EUR';
}

/**
 * The user's diets applied to food (rules in logic/diets.ts). `review` checks a recipe against each
 * diet; `issues` gives the marks: certain breaks (an ingredient) and unconfirmed warnings. With
 * "hide" on, food that certainly breaks a diet is `hidden`: it does not exist for this user, so
 * lists, search, pickers and path counters leave it out. Warnings never hide anything.
 * Allergies the ingredient data knows (gluten, milk) count as their diets (logic/dietPlan.ts).
 */
export function useDiet() {
  const chosen = useSettings(s => s.diets);
  const allergies = useSettings(s => s.allergies);
  const diets = useMemo(() => dietsWithAllergies(chosen, allergies), [chosen, allergies]);
  const restrict = useSettings(s => s.restrictDiet);
  const ingredients = useAllIngredients();
  const confirmations = useLibrary(s => s.dietConfirmations);
  return useMemo(() => {
    const cache = new WeakMap<Recipe, DietStatus[]>();
    const review = (r: Recipe | undefined): DietStatus[] => {
      if (!r || !diets.length) return [];
      let c = cache.get(r);
      if (!c) cache.set(r, (c = dietReview(r, ingredients, diets, confirmations[r.id])));
      return c;
    };
    const issues = (r: Recipe | undefined): DietFlags => dietFlags(review(r));
    const ingredientConflicts = (i: Ingredient | undefined): Diet[] => (i && diets.length ? dietConflicts(i.notDiet, diets) : []);
    const hiding = restrict && diets.length > 0;
    return {
      review,
      issues,
      ingredientConflicts,
      restricting: hiding,
      hidden: (r: Recipe | undefined) => hiding && issues(r).breaks.length > 0,
      ingredientHidden: (i: Ingredient | undefined) => hiding && ingredientConflicts(i).length > 0,
    };
  }, [diets, restrict, ingredients, confirmations]);
}

/** Holidays of the user's traditions that are on today or start within a week, soonest first. */
export function useHolidays(today: Day): HolidayNear[] {
  const traditions = useSettings(s => s.traditions);
  return useMemo(() => holidaysNear(today, traditions), [today, traditions]);
}

/**
 * Whole seconds left until `until` (ms epoch), re-rendering once a second; 0 when there is nothing to wait for.
 * Computed on every render (not kept in state) so a new `until` never shows a stale value for a moment.
 */
export function useCountdown(until: number | null | undefined): number {
  const [, tick] = useState(0);
  useEffect(() => {
    if (!until) return;
    const id = setInterval(() => {
      tick(n => n + 1);
      if (Date.now() >= until) clearInterval(id);
    }, 1000);
    return () => clearInterval(id);
  }, [until]);
  return until ? Math.max(0, Math.ceil((until - Date.now()) / 1000)) : 0;
}
